import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore from 'swiper/core'
import { Navigation, Pagination, Thumbs } from 'swiper/modules'
import { useTranslation } from 'next-i18next/pages'
import type { Maybe, ProductVideo, ProductImage } from '@pylot-data/pylotschema'
import React, { ReactElement, useMemo, useState, useEffect } from 'react'
import cn from 'classnames'
import s from './ScrollGallery.module.scss'
import swiperStyles from '@commonStyles/Swiper.module.scss'
import ImageSlide from '../util/ImageSlide'
import {
    getFirstGalleryImageIndex,
    getGalleryMediaKey
} from '../util/getGalleryMediaKey'
import dynamic from 'next/dynamic'
import VideoPlayer from '../VideoPlayer'

const FullscreenGallery = dynamic(() => import('../FullscreenGallery'))
// Swiper module registration (not a React hook)
// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Pagination, Thumbs])

type ProducGalleryType = {
    alt?: string
    mediaGalleryEntries: Maybe<ProductVideo | ProductImage>[]
    zoom: boolean
    showFullscreen: boolean
    thumb?: boolean
    sliderProps?: {
        main?: {
            loop?: boolean
            spaceBetween?: number
            navigation?: boolean
            pagination?:
                | boolean
                | {
                      clickable: boolean
                  }
        }
        thumbs?: {
            spaceBetween?: number
            slidesPerView?: number
        }
    }
    badgeLabel?: string
    badgeVariant?: 'low-stock'
}

const defaultSliderProps = {
    main: {
        spaceBetween: 10,
        navigation: true,
        pagination: {
            clickable: true
        },
        loop: false
    },
    thumbs: {
        spaceBetween: 10,
        slidesPerView: 8
    }
}

const ScrollGallery = ({
    alt,
    mediaGalleryEntries,
    zoom,
    thumb = false,
    sliderProps,
    badgeLabel,
    badgeVariant
}: ProducGalleryType): ReactElement => {
    const { t } = useTranslation(['common'])

    const [isFullscreen, setFullscreen] = useState(false)
    const closeFullscreen = () => setFullscreen(false)

    const [sliderIndex, setSliderIndex] = useState(0)

    const openFullscreen = (i: number) => {
        setSliderIndex(i)
        setFullscreen(true)
    }

    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>()
    const firstImageIndex = useMemo(
        () => getFirstGalleryImageIndex(mediaGalleryEntries),
        [mediaGalleryEntries]
    )
    const syncThumbnail = (swiperCore: SwiperCore, delay: number) => {
        //Quick fix to swiper bug
        setTimeout(() => swiperCore.thumbs.update(false), delay)
    }

    const renderImages = (
        mediaGalleryData: Maybe<ProductVideo | ProductImage>[],
        alt?: string,
        width = 1050,
        height = 1050,
        showFullscreenGallery = false,
        isThumbnail = false
    ) =>
        mediaGalleryData?.map((image, i) => (
            <SwiperSlide
                key={getGalleryMediaKey(image, i)}
                className={cn(s['scroll-image-container'], {
                    'video-thumb': image?.__typename === 'ProductVideo'
                })}
            >
                {image?.__typename === 'ProductVideo' ? (
                    <VideoPlayer
                        alt={alt || t('Product Thumbnail')}
                        url={`${image.video_content?.video_url!}`}
                        index={i}
                        image={image?.url}
                        label={image?.label}
                        isThumbnail={isThumbnail}
                    />
                ) : image?.arLink ? (
                    <iframe
                        title="test"
                        src={image?.url || ''}
                        frameBorder="0"
                    />
                ) : (
                    <ImageSlide
                        image={image}
                        alt={alt || t('Product Image')}
                        width={width}
                        height={height}
                        index={i}
                        priority={i === firstImageIndex}
                        zoom={zoom}
                        showFullscreen={showFullscreenGallery}
                        s={s}
                        openFullscreen={openFullscreen}
                    />
                )}
            </SwiperSlide>
        ))

    useEffect(() => {
        const html = document.getElementsByTagName('html')[0]
        html.classList.remove('zoom-open')
        if (isFullscreen) html.classList.add('zoom-open')
    }, [isFullscreen])

    return (
        <div className={s['pdp-wrapper']}>
            {badgeLabel && (
                <span
                    className={cn(s['pdp-badge'], {
                        [s['pdp-badge-low-stock']]: badgeVariant === 'low-stock'
                    })}
                >
                    {badgeLabel}
                </span>
            )}
            <div className={cn('pdp-slider', swiperStyles.root)}>
                {isFullscreen && (
                    <FullscreenGallery
                        alt={alt}
                        closeFullscreen={closeFullscreen}
                        mediaGalleryEntries={mediaGalleryEntries}
                        sliderIndex={sliderIndex}
                    />
                )}
                <div className={cn(s['pdp-slider-main'])}>
                    <Swiper
                        {...{
                            ...defaultSliderProps.main,
                            ...sliderProps?.main
                        }}
                        thumbs={{ swiper: thumbsSwiper }}
                        onSwiper={(swiperCore: SwiperCore) =>
                            syncThumbnail(swiperCore, 300)
                        }
                        onSlideChange={(swiperCore: SwiperCore) =>
                            syncThumbnail(swiperCore, 100)
                        }
                    >
                        {renderImages(
                            mediaGalleryEntries,
                            alt || t('Product Image'),
                            1050,
                            1050,
                            true
                        )}
                    </Swiper>
                    {thumb && (
                        <div className="thumbnails">
                            <Swiper
                                onSwiper={setThumbsSwiper}
                                {...{
                                    ...defaultSliderProps.thumbs,
                                    ...sliderProps?.thumbs
                                }}
                                freeMode
                                watchSlidesProgress
                            >
                                {renderImages(
                                    mediaGalleryEntries,
                                    alt || t('Product Image'),
                                    100,
                                    100,
                                    false,
                                    true
                                )}
                            </Swiper>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ScrollGallery
